import type {
    CafeCollaborationFormat,
    CafeVacancyEmploymentKind,
    CafeVacancyStatus,
    GrantableCafeRole,
    VacancyComplaintReason,
    VacancyComplaintStatus
} from '@cookspace-shared-contracts/core';

/**
 * RU-лейблы и палитры словарей жалоб/вакансий для admin-раздела «Жалобы на
 * вакансии» (staff-hiring Ф2). Дублирование меток ролей/специализаций —
 * принятый паттерн проекта (admin не зависит от backend; зеркало
 * `cafe-roles`/`vacancy-title`). Источник лейблов — design-spec §2.4/§2.5
 * и копирайтинг design.md §5.
 */

export const COMPLAINT_REASON_LABEL: Record<VacancyComplaintReason, string> = {
    discrimination: 'Дискриминационные требования',
    fraud: 'Похоже на мошенничество',
    spam: 'Спам или нерелевантное объявление',
    other: 'Другое'
};

export const COMPLAINT_STATUS_LABEL: Record<VacancyComplaintStatus, string> = {
    pending: 'Новая',
    vacancy_hidden: 'Вакансия скрыта',
    dismissed: 'Закрыта'
};

export const COMPLAINT_STATUS_COLOR: Record<VacancyComplaintStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    vacancy_hidden: 'bg-red-100 text-red-800',
    dismissed: 'bg-gray-100 text-gray-800'
};

/**
 * Лейблы операционных ролей кафе — зеркало `cafe-site/src/lib/cafe-roles.ts`
 * (`CAFE_ROLE_LABELS`), ограниченные grantable-набором вакансии.
 */
export const CAFE_ROLE_LABEL: Record<GrantableCafeRole, string> = {
    chef: 'Повар',
    barista: 'Бариста',
    bartender: 'Бармен',
    hookah_master: 'Кальянный мастер',
    waiter: 'Официант',
    courier: 'Курьер'
};

export const EMPLOYMENT_KIND_LABEL: Record<CafeVacancyEmploymentKind, string> = {
    shift: 'Смена',
    event: 'Ивент',
    permanent: 'Постоянная'
};

export const COLLABORATION_FORMAT_LABEL: Record<CafeCollaborationFormat, string> = {
    staff: 'Штат',
    gph: 'ГПХ',
    self_employed: 'Самозанятый'
};

export const VACANCY_STATUS_LABEL: Record<CafeVacancyStatus, string> = {
    draft: 'Черновик',
    published: 'Опубликована',
    closed: 'Закрыта',
    hidden_by_owner: 'Скрыта владельцем',
    hidden_by_moderator: 'Скрыта модератором'
};

/**
 * Единица вилки ставки — производная от типа занятости (design-spec общие
 * правила): за смену для `shift`/`event`, в месяц для `permanent`.
 */
function rateUnit(employmentKind: CafeVacancyEmploymentKind): string {
    return employmentKind === 'permanent' ? 'в месяц' : 'за смену';
}

/**
 * Форматирует вилку ставки из копеек в рубли: `2 500–3 500 ₽ за смену`.
 * Если границы равны — одно значение без диапазона.
 */
export function formatRateRange(
    rateFromKop: number,
    rateToKop: number,
    employmentKind: CafeVacancyEmploymentKind
): string {
    const fromRub = Math.round(rateFromKop / 100);
    const toRub = Math.round(rateToKop / 100);
    const unit = rateUnit(employmentKind);
    if (fromRub === toRub) {
        return `${fromRub.toLocaleString('ru-RU')} ₽ ${unit}`;
    }
    return `${fromRub.toLocaleString('ru-RU')}–${toRub.toLocaleString('ru-RU')} ₽ ${unit}`;
}
